import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Search, Download, Filter, ChevronRight, FileText } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import StatusPill from "@/components/aieco/StatusPill";
import { useWorkspace } from "@/context/WorkspaceContext";
import log from "@/lib/log";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RESULT_TONE = {
  OK: "success",
  Approved: "success",
  Declined: "neutral",
  Warning: "warning",
  Blocked: "critical",
  Violation: "critical",
  Escalated: "warning",
};
const HUMAN_TONE = { Auto: "info", Pending: "warning", "—": "neutral" };

export default function AuditCenter() {
  const { workspaceId, data } = useWorkspace();
  const [entries, setEntries] = useState([]);
  const [q, setQ] = useState("");
  const [resultFilter, setResultFilter] = useState("All");
  const [expanded, setExpanded] = useState(null);

  React.useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/audit`, { params: { workspace: workspaceId } }).then((r) => {
      if (!cancelled) setEntries(r.data.items || []);
    }).catch((e) => {
      log.warn("[audit] fetch failed:", e?.message || e);
      if (!cancelled) setEntries([]);
    });
    return () => { cancelled = true; };
  }, [workspaceId]);

  const filtered = useMemo(() => {
    return entries.filter((e) => {
      const matchesQ = !q || `${e.agent} ${e.action} ${e.policy} ${e.id}`.toLowerCase().includes(q.toLowerCase());
      const matchesR = resultFilter === "All" || e.result === resultFilter;
      return matchesQ && matchesR;
    });
  }, [entries, q, resultFilter]);

  const exportCsv = () => {
    const headers = ["Timestamp", "Agent", "Action", "Policy", "Risk", "Tokens", "Result", "Human"];
    const lines = filtered.map((e) => [e.time, e.agent, e.action, e.policy, e.risk, e.tokens, e.result, e.human].join(","));
    const blob = new Blob([[headers.join(","), ...lines].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `aieco-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPdf = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const today = new Date().toISOString().slice(0, 10);
    doc.setFillColor(11, 16, 32); doc.rect(0, 0, 842, 70, "F");
    doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold"); doc.setFontSize(16);
    doc.text("AI-ECO Suite", 40, 32);
    doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(180, 200, 240);
    doc.text(`Immutable Audit Report · ${data.name}`, 40, 50);
    doc.setTextColor(160, 170, 190);
    doc.text(`Generated: ${today}  ·  ${filtered.length} events`, 600, 50);
    doc.setDrawColor(91, 141, 239); doc.setLineWidth(0.6);
    doc.roundedRect(700, 14, 100, 28, 6, 6);
    doc.setFontSize(8); doc.setTextColor(91, 141, 239);
    doc.text("SIGNED · SHA-256", 712, 32);
    autoTable(doc, {
      startY: 90,
      head: [["Timestamp", "Agent", "Action", "Policy", "Risk", "Tokens", "Result", "Human"]],
      body: filtered.map((e) => [e.time, e.agent, e.action, e.policy, e.risk, e.tokens, e.result, e.human]),
      styles: { font: "helvetica", fontSize: 8, cellPadding: 6, lineColor: [30, 41, 71], lineWidth: 0.4 },
      headStyles: { fillColor: [26, 34, 56], textColor: 220, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [16, 22, 42] },
      bodyStyles: { textColor: 60 },
      theme: "grid",
      margin: { left: 40, right: 40 },
    });
    const pages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pages; i++) {
      doc.setPage(i); doc.setFontSize(8); doc.setTextColor(120);
      doc.text(`Page ${i} of ${pages}  ·  Cryptographically chained · ai-eco.io/verify`, 40, 580);
    }
    doc.save(`aieco-audit-${today}.pdf`);
  };

  return (
    <div className="mx-auto max-w-[1480px] px-6 py-8 lg:px-10 lg:py-10" data-testid="page-audit">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Audit Center · {data.name}</div>
          <h1 className="mt-2 font-display text-3xl font-medium tracking-tight text-white">
            Immutable record. Every action. Forever.
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-400">
            Cryptographically chained audit trail of every agent action, policy decision and human override — persisted server-side, exportable for regulators in one click.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} data-testid="export-report" aria-label="Export CSV" className="flex items-center gap-2 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2 text-[12px] text-slate-200 hover:border-white/10">
            <Download size={13} strokeWidth={2} /> Export CSV
          </button>
          <button onClick={exportPdf} data-testid="export-pdf" aria-label="Export signed PDF" className="flex items-center gap-2 rounded-lg bg-[#5B8DEF] px-3 py-2 text-[12px] font-medium text-[#0b1020] hover:bg-[#7AA4F5]">
            <FileText size={13} strokeWidth={2} /> Signed PDF
          </button>
        </div>
      </motion.div>

      <div className="card-eco mt-6 overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 border-b border-white/5 p-4">
          <div className="flex flex-1 items-center gap-2 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-1.5">
            <Search size={13} className="text-slate-500" aria-hidden="true" />
            <input
              data-testid="audit-search"
              aria-label="Search audit entries"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search agent, action, policy or event id…"
              className="w-full bg-transparent text-[12px] text-slate-200 placeholder:text-slate-500 focus:outline-none"
            />
          </div>
          <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
            <Filter size={12} aria-hidden="true" /> Result:
          </div>
          {["All", "OK", "Approved", "Warning", "Blocked", "Violation", "Escalated"].map((f) => (
            <button
              key={f}
              onClick={() => setResultFilter(f)}
              data-testid={`audit-filter-${f.toLowerCase()}`}
              aria-pressed={resultFilter === f}
              className={`rounded-lg border px-2.5 py-1 text-[11px] ${
                resultFilter === f
                  ? "border-[#5B8DEF]/50 bg-[#5B8DEF]/10 text-[#cfe0ff]"
                  : "border-white/5 bg-white/[0.02] text-slate-400 hover:text-slate-200"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        <table className="w-full text-left text-sm" aria-label="Audit trail">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.16em] text-slate-500">
              <th scope="col" className="px-4 py-3 font-medium">Timestamp</th>
              <th scope="col" className="px-4 py-3 font-medium">Agent</th>
              <th scope="col" className="px-4 py-3 font-medium">Action</th>
              <th scope="col" className="px-4 py-3 font-medium">Policy</th>
              <th scope="col" className="px-4 py-3 font-medium">Risk</th>
              <th scope="col" className="px-4 py-3 font-medium">Tokens</th>
              <th scope="col" className="px-4 py-3 font-medium">Result</th>
              <th scope="col" className="px-4 py-3 font-medium">Human</th>
              <th scope="col" className="px-4 py-3 font-medium" />
            </tr>
          </thead>
          <tbody>
            {filtered.map((e) => {
              const isOpen = expanded === e.id;
              return (
                <React.Fragment key={e.id}>
                  <tr className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`audit-row-${e.id}`}>
                    <td className="px-4 py-3 font-mono text-[12px] text-slate-300">{e.time}</td>
                    <td className="px-4 py-3 text-slate-200">{e.agent}</td>
                    <td className="px-4 py-3 font-mono text-[12px] text-slate-300">{e.action}</td>
                    <td className="px-4 py-3 text-[12px] text-slate-400">{e.policy}</td>
                    <td className="px-4 py-3 font-mono text-[12px]" style={{ color: e.risk >= 80 ? "#EF4444" : e.risk >= 60 ? "#F59E0B" : "#94A3B8" }}>
                      {e.risk}
                    </td>
                    <td className="px-4 py-3 font-mono text-[12px] text-slate-300">{Number(e.tokens).toLocaleString()}</td>
                    <td className="px-4 py-3"><StatusPill tone={RESULT_TONE[e.result] || "neutral"}>{e.result}</StatusPill></td>
                    <td className="px-4 py-3"><StatusPill tone={HUMAN_TONE[e.human] || "neutral"} dot={false}>{e.human}</StatusPill></td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setExpanded(isOpen ? null : e.id)}
                        aria-label={isOpen ? `Collapse ${e.id}` : `Expand ${e.id}`}
                        aria-expanded={isOpen}
                        className="text-[12px] text-slate-300 hover:text-white"
                        data-testid={`audit-expand-${e.id}`}
                      >
                        <ChevronRight size={14} className={`inline transition ${isOpen ? "rotate-90" : ""}`} aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-white/[0.015]">
                      <td colSpan={9} className="px-6 pb-4 pt-2">
                        <div className="border-l border-[#5B8DEF]/40 pl-4">
                          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                            <div>
                              <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Event ID</div>
                              <div className="font-mono text-[12px] text-slate-200">{e.id}</div>
                            </div>
                            <div>
                              <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Hash</div>
                              <div className="font-mono text-[12px] text-slate-200">0x{(e.id + e.time).replace(/[^0-9a-f]/gi, "").padEnd(12, "a").slice(0, 12)}…</div>
                            </div>
                            <div>
                              <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Chain</div>
                              <div className="font-mono text-[12px] text-emerald-300">verified ✓</div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && entries.length === 0 && (
          <div className="px-6 py-12 text-center text-sm text-slate-500" data-testid="audit-loading">Loading audit entries…</div>
        )}
      </div>

      <div className="card-eco mt-6 p-6" data-testid="audit-timeline">
        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Timeline</div>
        <div className="mt-1 font-display text-lg text-slate-100">Today</div>
        <ol className="relative mt-5 space-y-3 border-l border-white/5 pl-5">
          {filtered.slice(0, 8).map((e) => (
            <li key={e.id} className="relative" data-testid={`timeline-${e.id}`}>
              <span
                className="absolute -left-[27px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-[#0B1020]"
                style={{ background: e.risk >= 80 ? "#EF4444" : e.risk >= 60 ? "#F59E0B" : "#5B8DEF" }}
                aria-hidden="true"
              />
              <div className="flex items-baseline gap-3">
                <span className="font-mono text-[11px] text-slate-500">{e.time}</span>
                <span className="text-[13px] text-slate-200">{e.agent}</span>
                <span className="text-[12px] text-slate-500">— {e.action}</span>
              </div>
              <div className="text-[11px] text-slate-500">{e.policy}</div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
